'use client';

/**
 * Booking flow client — three-step state machine:
 *   1) "pick"    — choose a date + slot
 *   2) "details" — name/email/notes
 *   3) "done"    — confirmation screen with manage link
 *
 * Theming for embed-anywhere use:
 *   - Body is transparent so the host page's background shows through
 *   - Card colors are driven by CSS variables that follow the visitor's
 *     prefers-color-scheme (auto light/dark) by default
 *   - URL overrides:
 *       ?theme=light | ?theme=dark   — force a theme regardless of OS
 *       ?accent=0066ff               — paint the primary button in this hex
 *
 * Times are computed server-side in UTC and rendered here in the visitor's
 * local timezone via Intl.DateTimeFormat.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import Script from 'next/script';

interface MeetingType {
  slug: string;
  name: string;
  description: string | null;
  duration_minutes: number;
  timezone: string;
  location_type: 'phone' | 'google_meet' | 'zoom' | 'custom';
  confirmation_message: string | null;
  host_display_name: string | null;
  theme?: 'auto' | 'light' | 'dark';
}

interface Slot {
  start_at_utc: string;
  end_at_utc: string;
  start_local: string;
}

interface Props {
  apiBase: string;
  meetingType: MeetingType;
  contactToken: string | null;
  turnstileSiteKey: string;
}

type Step = 'pick' | 'details' | 'done';

function detectTz(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

function ymd(d: Date): string {
  const yr = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, '0');
  const da = String(d.getDate()).padStart(2, '0');
  return `${yr}-${mo}-${da}`;
}

function buildMonth(year: number, monthIndex: number): Date[] {
  const first = new Date(year, monthIndex, 1);
  const dow = (first.getDay() + 6) % 7; // 0=Mon
  const start = new Date(year, monthIndex, 1 - dow);
  const out: Date[] = [];
  for (let i = 0; i < 42; i += 1) {
    out.push(new Date(start.getFullYear(), start.getMonth(), start.getDate() + i));
  }
  return out;
}

function fmtMonth(d: Date): string {
  return d.toLocaleString(undefined, { month: 'long', year: 'numeric' });
}

function fmtTime(iso: string, tz: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, {
      hour: 'numeric',
      minute: '2-digit',
      timeZone: tz,
    });
  } catch {
    return iso;
  }
}

function fmtFullDateTime(iso: string, tz: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZoneName: 'short',
      timeZone: tz,
    });
  } catch {
    return iso;
  }
}

function listTimezones(): string[] {
  try {
    const all = Intl.supportedValuesOf?.('timeZone') as string[] | undefined;
    return all ?? ['UTC'];
  } catch {
    return ['UTC'];
  }
}

declare global {
  interface Window {
    turnstile?: {
      render: (selector: string | HTMLElement, opts: Record<string, unknown>) => string;
      reset: (id?: string) => void;
      getResponse: (id?: string) => string | undefined;
    };
  }
}

// CSS variable theme. The shell paints its own page background so the
// surrounding area inside the iframe matches the chosen theme (instead of
// inheriting whatever the host site or app shell painted on <body>).
// Light is the default; @media (prefers-color-scheme: dark) flips to dark
// unless the parent has data-theme="light" (URL override).
const THEME_CSS = `
:root, html, body { background: transparent !important; }
body { margin: 0; }
.bk-shell {
  --bk-page-bg: #f8fafc;
  --bk-card: #ffffff;
  --bk-card-border: rgba(15, 23, 42, 0.10);
  --bk-card-shadow: 0 4px 24px rgba(15, 23, 42, 0.06);
  --bk-text: #0f172a;
  --bk-muted: #64748b;
  --bk-muted-2: #94a3b8;
  --bk-divider: rgba(15, 23, 42, 0.06);
  --bk-input-bg: #ffffff;
  --bk-input-border: rgba(15, 23, 42, 0.12);
  --bk-input-focus: var(--bk-accent);
  --bk-slot-border: rgba(15, 23, 42, 0.12);
  --bk-day-hover: rgba(15, 23, 42, 0.06);
  --bk-accent: #0f172a;
  --bk-accent-hover: #1e293b;
  --bk-accent-text: #ffffff;
  --bk-error: #dc2626;
  --bk-error-bg: rgba(220, 38, 38, 0.08);
  --bk-success: #16a34a;
  --bk-success-bg: rgba(22, 163, 74, 0.10);
  background: var(--bk-page-bg);
  color: var(--bk-text);
  color-scheme: light;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
}
@media (prefers-color-scheme: dark) {
  .bk-shell:not([data-theme="light"]) {
    --bk-page-bg: #0b1020;
    --bk-card: #161b2c;
    --bk-card-border: rgba(255, 255, 255, 0.08);
    --bk-card-shadow: 0 4px 24px rgba(0, 0, 0, 0.30);
    --bk-text: #f8fafc;
    --bk-muted: #94a3b8;
    --bk-muted-2: #64748b;
    --bk-divider: rgba(255, 255, 255, 0.06);
    --bk-input-bg: rgba(255, 255, 255, 0.04);
    --bk-input-border: rgba(255, 255, 255, 0.12);
    --bk-slot-border: rgba(255, 255, 255, 0.12);
    --bk-day-hover: rgba(255, 255, 255, 0.06);
    --bk-accent: #f8fafc;
    --bk-accent-hover: #e2e8f0;
    --bk-accent-text: #0b1020;
    --bk-error: #fca5a5;
    --bk-error-bg: rgba(252, 165, 165, 0.10);
    --bk-success: #86efac;
    --bk-success-bg: rgba(134, 239, 172, 0.10);
    color-scheme: dark;
  }
}
.bk-shell[data-theme="dark"] {
  --bk-page-bg: #0b1020;
  --bk-card: #161b2c;
  --bk-card-border: rgba(255, 255, 255, 0.08);
  --bk-card-shadow: 0 4px 24px rgba(0, 0, 0, 0.30);
  --bk-text: #f8fafc;
  --bk-muted: #94a3b8;
  --bk-muted-2: #64748b;
  --bk-divider: rgba(255, 255, 255, 0.06);
  --bk-input-bg: rgba(255, 255, 255, 0.04);
  --bk-input-border: rgba(255, 255, 255, 0.12);
  --bk-slot-border: rgba(255, 255, 255, 0.12);
  --bk-day-hover: rgba(255, 255, 255, 0.06);
  --bk-accent: #f8fafc;
  --bk-accent-hover: #e2e8f0;
  --bk-accent-text: #0b1020;
  --bk-error: #fca5a5;
  --bk-error-bg: rgba(252, 165, 165, 0.10);
  --bk-success: #86efac;
  --bk-success-bg: rgba(134, 239, 172, 0.10);
  color-scheme: dark;
}
.bk-card {
  background: var(--bk-card);
  border: 1px solid var(--bk-card-border);
  border-radius: 16px;
  box-shadow: var(--bk-card-shadow);
  overflow: hidden;
}
.bk-card-section { padding: 24px 28px; }
@media (max-width: 640px) { .bk-card-section { padding: 20px; } }
.bk-divider-b { border-bottom: 1px solid var(--bk-divider); }
.bk-divider-r { border-right: 1px solid var(--bk-divider); }
.bk-h1 { font-size: 22px; font-weight: 600; margin: 0; }
.bk-muted { color: var(--bk-muted); font-size: 14px; }
.bk-muted-sm { color: var(--bk-muted); font-size: 12px; }
.bk-input, .bk-textarea, .bk-select {
  width: 100%;
  padding: 9px 12px;
  font-size: 14px;
  background: var(--bk-input-bg);
  color: var(--bk-text);
  border: 1px solid var(--bk-input-border);
  border-radius: 8px;
  outline: none;
  font-family: inherit;
}
.bk-textarea { resize: vertical; min-height: 88px; }
.bk-input:focus, .bk-textarea:focus, .bk-select:focus {
  border-color: var(--bk-accent);
}
.bk-label { display: block; font-size: 13px; font-weight: 500; margin-bottom: 6px; color: var(--bk-text); }
.bk-btn {
  display: inline-block;
  padding: 10px 18px;
  font-size: 14px;
  font-weight: 500;
  background: var(--bk-accent);
  color: var(--bk-accent-text);
  border: 1px solid var(--bk-accent);
  border-radius: 8px;
  cursor: pointer;
  transition: opacity .15s;
}
.bk-btn:hover { opacity: 0.9; }
.bk-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.bk-btn-block { width: 100%; padding: 11px 16px; }
.bk-btn-ghost {
  background: transparent;
  color: var(--bk-text);
  border: 1px solid var(--bk-input-border);
  padding: 8px 14px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 13px;
  text-decoration: none;
}
.bk-btn-ghost:hover { border-color: var(--bk-accent); }
.bk-link {
  background: transparent;
  color: var(--bk-muted);
  border: none;
  padding: 0;
  cursor: pointer;
  font-size: 13px;
}
.bk-link:hover { color: var(--bk-text); }
.bk-cal-nav {
  display: flex; align-items: center; justify-content: space-between;
  margin-bottom: 12px;
}
.bk-cal-nav button {
  background: transparent; border: none; color: var(--bk-muted);
  font-size: 18px; cursor: pointer; padding: 4px 8px; border-radius: 6px;
}
.bk-cal-nav button:hover { color: var(--bk-text); background: var(--bk-day-hover); }
.bk-cal-grid {
  display: grid; grid-template-columns: repeat(7, 1fr); gap: 4px;
}
.bk-cal-dow { text-align: center; font-size: 11px; color: var(--bk-muted-2); padding: 4px 0; }
.bk-cal-day {
  aspect-ratio: 1; display: flex; align-items: center; justify-content: center;
  font-size: 13px; border-radius: 8px; border: none; background: transparent;
  color: var(--bk-text); cursor: pointer;
}
.bk-cal-day.bk-cal-day-out { color: var(--bk-muted-2); opacity: 0.45; }
.bk-cal-day:disabled { color: var(--bk-muted-2); opacity: 0.35; cursor: not-allowed; }
.bk-cal-day:not(:disabled):hover { background: var(--bk-day-hover); }
.bk-cal-day-active, .bk-cal-day-active:hover {
  background: var(--bk-accent); color: var(--bk-accent-text);
}
.bk-slot-btn {
  display: block; width: 100%; padding: 12px 16px; margin-bottom: 8px;
  text-align: left; font-weight: 500; font-size: 14px;
  background: transparent; color: var(--bk-text);
  border: 1px solid var(--bk-slot-border); border-radius: 10px; cursor: pointer;
  transition: border-color .15s;
}
.bk-slot-btn:hover { border-color: var(--bk-accent); }
.bk-banner-error {
  background: var(--bk-error-bg); color: var(--bk-error);
  padding: 10px 14px; border-radius: 8px; font-size: 13px; margin-bottom: 12px;
}
.bk-success-pill {
  display: inline-flex; align-items: center; justify-content: center;
  width: 48px; height: 48px; border-radius: 999px;
  background: var(--bk-success-bg); color: var(--bk-success);
  font-size: 22px; margin: 0 auto 12px;
}
.bk-shell ::-webkit-calendar-picker-indicator { filter: var(--bk-picker-filter, none); }
.bk-shell[data-theme="dark"] ::-webkit-calendar-picker-indicator,
@media (prefers-color-scheme: dark) {
  .bk-shell:not([data-theme="light"]) ::-webkit-calendar-picker-indicator { filter: invert(1); }
}
`;

function applyAccent(accent: string | null): string {
  if (!accent) return '';
  // Accept hex with or without leading #
  const cleaned = accent.replace(/^#/, '');
  if (!/^[0-9a-f]{3}([0-9a-f]{3})?$/i.test(cleaned)) return '';
  const hex = cleaned.length === 3
    ? cleaned.split('').map((c) => c + c).join('')
    : cleaned;
  return `.bk-shell { --bk-accent: #${hex}; --bk-accent-hover: #${hex}; --bk-accent-text: #ffffff; }`;
}

export default function BookingClient({
  apiBase,
  meetingType,
  contactToken,
  turnstileSiteKey,
}: Props) {
  const searchParams = useSearchParams();
  const themeOverride = searchParams.get('theme');
  const accentOverride = searchParams.get('accent');
  // URL ?theme= wins; otherwise fall back to the operator's saved choice on
  // the meeting type. "auto" means leave data-theme unset so the CSS @media
  // (prefers-color-scheme) rule follows the visitor's OS preference.
  const urlTheme = themeOverride === 'dark' || themeOverride === 'light' ? themeOverride : null;
  const savedTheme = meetingType.theme === 'light' || meetingType.theme === 'dark' ? meetingType.theme : null;
  const dataTheme = urlTheme ?? savedTheme ?? undefined;
  const accentCss = useMemo(() => applyAccent(accentOverride), [accentOverride]);

  // Marketing attribution: pull UTMs off the page URL so the activity
  // timeline can show where this lead came from. Captured client-side after
  // mount (the iframe's parent URL params land in window.location.search).
  const [utm, setUtm] = useState<Record<string, string>>({});
  const [pageUrl, setPageUrl] = useState<string | null>(null);
  const [referrer, setReferrer] = useState<string | null>(null);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const sp = new URL(window.location.href).searchParams;
      const captured: Record<string, string> = {};
      for (const k of ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term']) {
        const v = sp.get(k);
        if (v) captured[k] = v;
      }
      if (Object.keys(captured).length > 0) setUtm(captured);
      setPageUrl(window.location.href);
      setReferrer(document.referrer || null);
    } catch { /* noop */ }
  }, []);

  // When embedded in an iframe, tell the parent how tall we are so it can
  // resize the frame to fit (no internal scrollbar). The host page is
  // expected to listen for `{ type: 'gzoo-booking-resize', height }` and
  // validate event.origin on its side.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (window.parent === window) return;
    const post = () => {
      const height = document.documentElement.scrollHeight;
      window.parent.postMessage({ type: 'gzoo-booking-resize', height }, '*');
    };
    post();
    const ro = new ResizeObserver(post);
    ro.observe(document.documentElement);
    return () => ro.disconnect();
  }, []);

  // Defer all client-only state (tz, current date) until after mount so the
  // server-rendered HTML matches the first client render. detectTz() and
  // `new Date()` produce different values on server (UTC) vs client (user
  // tz / clock), which would otherwise trip React's hydration check (#418).
  const [mounted, setMounted] = useState(false);
  const [tz, setTz] = useState<string>('UTC');
  const [step, setStep] = useState<Step>('pick');
  const [monthAnchor, setMonthAnchor] = useState<Date>(new Date(Date.UTC(2000, 0, 1)));
  const [slots, setSlots] = useState<Slot[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const now = new Date();
    setTz(detectTz());
    setMonthAnchor(new Date(now.getFullYear(), now.getMonth(), 1));
    setMounted(true);
  }, []);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Slot | null>(null);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState<{
    bookingId: string;
    manageToken: string;
    startAtUtc: string;
    endAtUtc: string;
    confirmationMessage: string | null;
    locationType: string;
    locationValue: string | null;
  } | null>(null);

  const turnstileWidgetIdRef = useRef<string | null>(null);

  const monthDays = useMemo(
    () => buildMonth(monthAnchor.getFullYear(), monthAnchor.getMonth()),
    [monthAnchor]
  );

  const tzOptions = useMemo(() => listTimezones(), []);

  const fetchSlots = useCallback(async () => {
    if (!mounted) return;
    setLoading(true);
    setError(null);
    try {
      const from = ymd(monthDays[0]);
      const to = ymd(monthDays[monthDays.length - 1]);
      const url = `${apiBase}/booking/types/${encodeURIComponent(meetingType.slug)}/availability?from=${from}&to=${to}&tz=${encodeURIComponent(tz)}`;
      const r = await fetch(url, { cache: 'no-store' });
      if (!r.ok) throw new Error(`Failed to load availability (${r.status})`);
      const data = (await r.json()) as { slots: Slot[] };
      setSlots(data.slots || []);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [apiBase, meetingType.slug, monthDays, tz, mounted]);

  useEffect(() => {
    void fetchSlots();
  }, [fetchSlots]);

  const slotsByDate = useMemo(() => {
    const m = new Map<string, Slot[]>();
    for (const s of slots) {
      const d = new Date(s.start_at_utc).toLocaleDateString(undefined, {
        year: 'numeric', month: '2-digit', day: '2-digit', timeZone: tz,
      });
      const arr = m.get(d) || [];
      arr.push(s);
      m.set(d, arr);
    }
    return m;
  }, [slots, tz]);

  const [activeDay, setActiveDay] = useState<string | null>(null);
  useEffect(() => {
    const firstWithSlots = monthDays.find((d) => {
      const key = d.toLocaleDateString(undefined, {
        year: 'numeric', month: '2-digit', day: '2-digit', timeZone: tz,
      });
      return slotsByDate.has(key);
    });
    if (firstWithSlots) {
      setActiveDay(
        firstWithSlots.toLocaleDateString(undefined, {
          year: 'numeric', month: '2-digit', day: '2-digit', timeZone: tz,
        })
      );
    } else {
      setActiveDay(null);
    }
  }, [slotsByDate, monthDays, tz]);

  const renderTurnstile = useCallback(() => {
    if (!turnstileSiteKey || !window.turnstile) return;
    if (turnstileWidgetIdRef.current) return;
    const el = document.getElementById('booking-turnstile');
    if (!el) return;
    try {
      const id = window.turnstile.render(el, {
        sitekey: turnstileSiteKey,
        size: 'normal',
        theme: 'auto', // follows prefers-color-scheme
      });
      turnstileWidgetIdRef.current = id;
    } catch {
      /* re-renders */
    }
  }, [turnstileSiteKey]);

  useEffect(() => {
    if (step === 'details') {
      const t = setTimeout(renderTurnstile, 200);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [step, renderTurnstile]);

  const submitBooking = async () => {
    if (!selected) return;
    if (!name.trim() || !email.trim()) {
      setError('Please enter your name and email');
      return;
    }
    let turnstileToken: string | undefined;
    if (turnstileSiteKey && window.turnstile && turnstileWidgetIdRef.current) {
      turnstileToken = window.turnstile.getResponse(turnstileWidgetIdRef.current);
      if (!turnstileToken) {
        setError('Please complete the security check');
        return;
      }
    }
    setSubmitting(true);
    setError(null);
    try {
      const r = await fetch(
        `${apiBase}/booking/types/${encodeURIComponent(meetingType.slug)}/book`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            start_at_utc: selected.start_at_utc,
            name: name.trim(),
            email: email.trim(),
            notes: notes.trim() || null,
            visitor_timezone: tz,
            turnstile_token: turnstileToken,
            contact_token: contactToken,
            utm: Object.keys(utm).length > 0 ? utm : null,
            page_url: pageUrl,
            referrer: referrer,
          }),
        }
      );
      if (!r.ok) {
        const detail = await r.json().catch(() => ({ detail: 'Booking failed' }));
        throw new Error(detail.detail || 'Booking failed');
      }
      const data = await r.json();
      setConfirmation({
        bookingId: data.booking_id,
        manageToken: data.manage_token,
        startAtUtc: data.start_at_utc,
        endAtUtc: data.end_at_utc,
        confirmationMessage: data.confirmation_message,
        locationType: data.location_type,
        locationValue: data.location_value,
      });
      setStep('done');
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      if (window.turnstile && turnstileWidgetIdRef.current) {
        try { window.turnstile.reset(turnstileWidgetIdRef.current); } catch { /* */ }
      }
    } finally {
      setSubmitting(false);
    }
  };

  const dayKey = (d: Date) =>
    d.toLocaleDateString(undefined, {
      year: 'numeric', month: '2-digit', day: '2-digit', timeZone: tz,
    });

  const slotsForActive = activeDay ? slotsByDate.get(activeDay) || [] : [];

  return (
    <div className="bk-shell" data-theme={dataTheme} style={{ padding: '24px 16px', minHeight: '100vh' }}>
      <style dangerouslySetInnerHTML={{ __html: THEME_CSS + accentCss }} />
      {turnstileSiteKey && (
        <Script
          src="https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit"
          strategy="afterInteractive"
        />
      )}
      <div className="bk-card" style={{ maxWidth: 760, margin: '0 auto' }}>
        <header className="bk-card-section bk-divider-b">
          <h1 className="bk-h1">{meetingType.name}</h1>
          <p className="bk-muted" style={{ marginTop: 4 }}>
            {meetingType.duration_minutes} min
            {meetingType.host_display_name && !meetingType.host_display_name.includes('@')
              ? ` · with ${meetingType.host_display_name}`
              : ''}
          </p>
          {meetingType.description && (
            <p style={{ marginTop: 12, whiteSpace: 'pre-wrap' }}>{meetingType.description}</p>
          )}
        </header>

        {!mounted && (
          <div className="bk-card-section bk-muted" style={{ textAlign: 'center', padding: '48px 28px' }}>
            Loading…
          </div>
        )}

        {mounted && step === 'pick' && (
          <section style={{ display: 'grid', gridTemplateColumns: 'minmax(260px, 1fr) minmax(220px, 1fr)' }}>
            <div className="bk-card-section bk-divider-r">
              <div className="bk-cal-nav">
                <button
                  type="button"
                  onClick={() => setMonthAnchor(new Date(monthAnchor.getFullYear(), monthAnchor.getMonth() - 1, 1))}
                  aria-label="Previous month"
                >‹</button>
                <div style={{ fontWeight: 500, fontSize: 14 }}>{fmtMonth(monthAnchor)}</div>
                <button
                  type="button"
                  onClick={() => setMonthAnchor(new Date(monthAnchor.getFullYear(), monthAnchor.getMonth() + 1, 1))}
                  aria-label="Next month"
                >›</button>
              </div>
              <div className="bk-cal-grid" style={{ marginBottom: 4 }}>
                {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((d, i) => (
                  <div key={i} className="bk-cal-dow">{d}</div>
                ))}
              </div>
              <div className="bk-cal-grid">
                {monthDays.map((d) => {
                  const key = dayKey(d);
                  const inMonth = d.getMonth() === monthAnchor.getMonth();
                  const has = slotsByDate.has(key);
                  const isActive = activeDay === key;
                  const cls = ['bk-cal-day'];
                  if (!inMonth) cls.push('bk-cal-day-out');
                  if (isActive) cls.push('bk-cal-day-active');
                  return (
                    <button
                      key={key + d.toISOString()}
                      type="button"
                      disabled={!has}
                      onClick={() => setActiveDay(key)}
                      className={cls.join(' ')}
                    >{d.getDate()}</button>
                  );
                })}
              </div>
              <div style={{ marginTop: 18 }}>
                <label className="bk-label" style={{ fontSize: 12 }}>Time zone</label>
                <select className="bk-select" value={tz} onChange={(e) => setTz(e.target.value)}>
                  {tzOptions.map((z) => (<option key={z} value={z}>{z}</option>))}
                </select>
              </div>
            </div>
            <div className="bk-card-section">
              <div className="bk-muted" style={{ marginBottom: 12 }}>
                {activeDay
                  ? new Date(slotsForActive[0]?.start_at_utc || Date.now()).toLocaleDateString(undefined, {
                      weekday: 'long', month: 'long', day: 'numeric', timeZone: tz,
                    })
                  : 'Pick a date'}
              </div>
              {loading && <div className="bk-muted">Loading times…</div>}
              {error && <div className="bk-banner-error">{error}</div>}
              {!loading && activeDay && slotsForActive.length === 0 && (
                <div className="bk-muted">No times available on this day.</div>
              )}
              {slotsForActive.map((s) => (
                <button
                  key={s.start_at_utc}
                  type="button"
                  className="bk-slot-btn"
                  onClick={() => { setSelected(s); setStep('details'); }}
                >{fmtTime(s.start_at_utc, tz)}</button>
              ))}
            </div>
          </section>
        )}

        {mounted && step === 'details' && selected && (
          <section className="bk-card-section">
            <button type="button" className="bk-link" style={{ marginBottom: 16 }} onClick={() => setStep('pick')}>
              ← Back to times
            </button>
            <div className="bk-muted" style={{ marginBottom: 4 }}>Selected time</div>
            <div style={{ fontWeight: 500, marginBottom: 24 }}>{fmtFullDateTime(selected.start_at_utc, tz)}</div>
            {error && <div className="bk-banner-error">{error}</div>}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div>
                <label className="bk-label">Name</label>
                <input className="bk-input" type="text" value={name} onChange={(e) => setName(e.target.value)} required />
              </div>
              <div>
                <label className="bk-label">Email</label>
                <input className="bk-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
                {contactToken && !email && (
                  <div className="bk-muted-sm" style={{ marginTop: 4 }}>
                    Pre-fill from your earlier form will be applied on submit.
                  </div>
                )}
              </div>
              <div>
                <label className="bk-label">
                  Anything to share before the meeting?{' '}
                  <span style={{ color: 'var(--bk-muted-2)', fontWeight: 400 }}>(optional)</span>
                </label>
                <textarea className="bk-textarea" value={notes} onChange={(e) => setNotes(e.target.value)} rows={4} />
              </div>
              {turnstileSiteKey && <div id="booking-turnstile" style={{ marginTop: 8 }} />}
              <button type="button" className="bk-btn bk-btn-block" disabled={submitting} onClick={submitBooking}>
                {submitting ? 'Booking…' : 'Confirm booking'}
              </button>
            </div>
          </section>
        )}

        {mounted && step === 'done' && confirmation && (
          <section className="bk-card-section" style={{ textAlign: 'center', padding: '40px 28px' }}>
            <div className="bk-success-pill">✓</div>
            <h2 style={{ fontSize: 18, fontWeight: 600, margin: '0 0 8px' }}>You&apos;re booked</h2>
            <div style={{ marginBottom: 24 }}>{fmtFullDateTime(confirmation.startAtUtc, tz)}</div>
            {confirmation.confirmationMessage && (
              <p className="bk-muted" style={{ maxWidth: 420, margin: '0 auto 24px', whiteSpace: 'pre-wrap' }}>
                {confirmation.confirmationMessage}
              </p>
            )}
            <p className="bk-muted-sm" style={{ marginBottom: 24 }}>
              We&apos;ve sent you a confirmation email with a calendar invite.
            </p>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
              <a className="bk-btn-ghost" href={`${apiBase}/booking/manage/${encodeURIComponent(confirmation.manageToken)}/ics`}>
                Add to calendar (.ics)
              </a>
              <a className="bk-btn-ghost" href={`/book/${encodeURIComponent(meetingType.slug)}/manage/${encodeURIComponent(confirmation.manageToken)}`}>
                Reschedule or cancel
              </a>
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
